import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { DialogFooter } from "@/components/ui/dialog";
import { useDispatch, useSelector } from "react-redux";
import { signup, checkCompanyNameExists } from "../../store/auth/authActions";
import {
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const SignupForm = ({ onCancel }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, isCheckLoading, companyExists } = useSelector(
    (state) => state.auth,
  );

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    userType: "buyer",
    name: "",
    companyName: "",
    postalCode: "",
    termsAccepted: false,
  });

  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false,
  });

  const [touchedFields, setTouchedFields] = useState({
    email: false,
    password: false,
    confirmPassword: false,
    name: false,
    companyName: false,
    postalCode: false,
    termsAccepted: false,
  });

  const [errors, setErrors] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    companyName: "",
    postalCode: "",
    termsAccepted: "",
  });

  const debounceTimerRef = useRef(null);
  const [companyCheckStatus, setCompanyCheckStatus] = useState(null);

  const emailRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const checkCompanyName = useCallback(
    (companyName) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (!companyName.trim()) {
        setCompanyCheckStatus(null);
        return;
      }

      setCompanyCheckStatus("checking");

      debounceTimerRef.current = setTimeout(() => {
        if (companyName.trim() && formData.userType === "seller") {
          dispatch(checkCompanyNameExists(companyName.trim()));
        }
      }, 700);
    },
    [dispatch, formData.userType],
  );

  useEffect(() => {
    if (
      touchedFields.companyName &&
      formData.companyName.trim() &&
      formData.userType === "seller"
    ) {
      if (companyExists === true) {
        setCompanyCheckStatus("taken");
        setErrors((prev) => ({
          ...prev,
          companyName:
            "This company name is already registered. Please choose a different name.",
        }));
      } else if (companyExists === false && !isCheckLoading) {
        setCompanyCheckStatus("available");
        setErrors((prev) => ({
          ...prev,
          companyName: "",
        }));
      }
    } else if (
      !formData.companyName.trim() &&
      touchedFields.companyName &&
      formData.userType === "seller"
    ) {
      setCompanyCheckStatus(null);
    }
  }, [
    companyExists,
    isCheckLoading,
    touchedFields.companyName,
    formData.companyName,
    formData.userType,
  ]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const newErrors = { ...errors };
    if (touchedFields.email) {
      if (!formData.email) {
        newErrors.email = "Email is required";
      } else if (!emailRegex.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      } else {
        newErrors.email = "";
      }
    }

    if (touchedFields.password) {
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 6) {
        newErrors.password = "Password must be at least 6 characters long";
      } else if (formData.password.length > 16) {
        newErrors.password = "Password must be less than 16 characters long";
      } else if (!/[A-Z]/.test(formData.password)) {
        newErrors.password =
          "Password must contain at least one uppercase letter";
      } else if (!/[a-z]/.test(formData.password)) {
        newErrors.password =
          "Password must contain at least one lowercase letter";
      } else if (!/[0-9]/.test(formData.password)) {
        newErrors.password = "Password must contain at least one number";
      } else if (!/[!@#$%^&*(),.?\":{}|<>]/.test(formData.password)) {
        newErrors.password =
          "Password must include at least one special character (!@#$%^&*)";
      } else {
        newErrors.password = "";
      }
    }

    if (touchedFields.confirmPassword) {
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Please confirm your password";
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      } else {
        newErrors.confirmPassword = "";
      }
    }

    if (touchedFields.name) {
      const nameValue = formData.name.trim();
      const nameRegex = /^[A-Za-z\s]+$/;

      if (!nameValue) {
        newErrors.name =
          formData.userType === "buyer"
            ? "Full name is required"
            : "Contact person name is required";
      } else if (nameValue.length < 4) {
        newErrors.name = "Name must be at least 4 characters long";
      } else if (!nameRegex.test(nameValue)) {
        newErrors.name = "Name can only contain alphabets and spaces";
      } else {
        newErrors.name = "";
      }
    }

    if (formData.userType === "seller" && touchedFields.companyName) {
      if (!formData.companyName.trim()) {
        newErrors.companyName = "Company name is required for sellers";
      } else if (companyExists === true) {
        newErrors.companyName =
          "This company name is already registered. Please choose a different name.";
      } else if (companyExists === false && !isCheckLoading) {
        newErrors.companyName = "";
      }
    }

    if (formData.userType === "seller" && touchedFields.postalCode) {
      const postalValue = formData.postalCode.trim();
      const postalRegex = /^[0-9-]+$/;

      if (!postalValue) {
        newErrors.postalCode = "Postal code is required";
      } else if (!postalRegex.test(postalValue)) {
        newErrors.postalCode =
          "Postal code can only contain numbers and hyphens";
      } else if (postalValue.length < 4 || postalValue.length > 10) {
        newErrors.postalCode =
          "Postal code must be between 4 and 10 characters";
      } else {
        newErrors.postalCode = "";
      }
    }

    if (touchedFields.termsAccepted) {
      if (!formData.termsAccepted) {
        newErrors.termsAccepted = "You must accept the Terms and Conditions";
      } else {
        newErrors.termsAccepted = "";
      }
    }

    setErrors(newErrors);
  }, [formData, touchedFields, companyExists, isCheckLoading]);

  const handleInputChange = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));

      if (!touchedFields[field]) {
        setTouchedFields((prev) => ({ ...prev, [field]: true }));
      }

      if (field === "companyName" && formData.userType === "seller") {
        checkCompanyName(value);
      }
    },
    [touchedFields, checkCompanyName, formData.userType],
  );

  const handleBlur = useCallback((field) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  }, []);

  const handleUserTypeChange = useCallback((value) => {
    setFormData((prev) => ({ ...prev, userType: value }));
    setCompanyCheckStatus(null);
  }, []);

  const handleTermsChange = useCallback((checked) => {
    setFormData((prev) => ({ ...prev, termsAccepted: checked }));
    setTouchedFields((prev) => ({ ...prev, termsAccepted: true }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      email: "",
      password: "",
      confirmPassword: "",
      userType: "buyer",
      name: "",
      companyName: "",
      postalCode: "",
      termsAccepted: false,
    });
    setTouchedFields({
      email: false,
      password: false,
      confirmPassword: false,
      name: false,
      companyName: false,
      postalCode: false,
      termsAccepted: false,
    });
    setErrors({
      email: "",
      password: "",
      confirmPassword: "",
      name: "",
      companyName: "",
      postalCode: "",
      termsAccepted: "",
    });
    setCompanyCheckStatus(null);
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault();

    const allFieldsTouched = Object.keys(touchedFields).reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});
    setTouchedFields(allFieldsTouched);

    const hasErrors = Object.values(errors).some((error) => error !== "");
    if (
      hasErrors ||
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword ||
      !formData.name ||
      (formData.userType === "seller" &&
        (!formData.companyName || !formData.postalCode)) ||
      !formData.termsAccepted ||
      companyExists === true
    ) {
      if (companyExists === true) {
        toast({
          title: "Company Name Unavailable",
          description:
            "This company name is already registered. Please choose a different name.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Validation Error",
          description: "Please fix all errors before submitting the form.",
          variant: "destructive",
        });
      }
      return;
    }

    try {
      const newUserData = {
        email: formData.email,
        password: formData.password,
        role: formData.userType,
        name: formData.name.trim(),
        companyName:
          formData.userType === "seller"
            ? formData.companyName.trim()
            : undefined,
        postalCode:
          formData.userType === "seller"
            ? formData.postalCode.trim()
            : undefined,
      };

      await dispatch(
        signup({
          signupData: newUserData,
        }),
      ).unwrap();
      resetForm();
    } catch (err) {
      console.error("Signup failed:", err);
    }
  };

  const handleShowPassword = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const isFormValid =
    formData.email.trim() &&
    formData.password.trim() &&
    formData.confirmPassword.trim() &&
    formData.name.trim() &&
    (formData.userType === "buyer" ||
      (formData.companyName.trim() && formData.postalCode.trim())) &&
    formData.termsAccepted &&
    companyExists !== true &&
    !isCheckLoading &&
    Object.values(errors).every((error) => error === "");

  const getCompanyNameBorderClass = () => {
    if (!touchedFields.companyName || !formData.companyName.trim()) return "";
    if (companyCheckStatus === "taken")
      return "border-red-500 focus:ring-red-500";
    if (companyCheckStatus === "available")
      return "border-green-500 focus:ring-green-500";
    if (companyCheckStatus === "checking")
      return "border-blue-400 focus:ring-blue-400";
    return "";
  };

  return (
    <form onSubmit={handleSignup} className="space-y-5">
      <div className="space-y-3">
        <Label className="text-sm font-medium">I am a:</Label>
        <RadioGroup
          value={formData.userType}
          onValueChange={handleUserTypeChange}
          className="flex space-x-6"
          disabled={isLoading}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="buyer" id="r1-signup-role" />
            <Label htmlFor="r1-signup-role" className="cursor-pointer">
              Buyer
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="seller" id="r2-signup-role" />
            <Label htmlFor="r2-signup-role" className="cursor-pointer">
              Seller
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-name" className="text-sm font-medium">
          {formData.userType === "buyer" ? "Full Name" : "Contact Person Name"}
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <Input
          id="signup-name"
          type="text"
          placeholder={
            formData.userType === "buyer" ? "John Doe" : "Jane Smith"
          }
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          onBlur={() => handleBlur("name")}
          required
          disabled={isLoading}
          className={errors.name ? "border-red-500 focus:ring-red-500" : ""}
        />
        <AnimatePresence>
          {errors.name && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-start gap-1.5 text-xs text-red-500"
            >
              <XCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{errors.name}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {formData.userType === "seller" && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="signup-companyName" className="text-sm font-medium">
              Company Name
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <div className="relative">
              <Input
                id="signup-companyName"
                type="text"
                placeholder="Enter your company name"
                value={formData.companyName}
                onChange={(e) =>
                  handleInputChange("companyName", e.target.value)
                }
                onBlur={() => handleBlur("companyName")}
                required
                disabled={isLoading}
                className={`pr-10 ${getCompanyNameBorderClass()}`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {companyCheckStatus === "checking" && (
                  <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                )}
                {companyCheckStatus === "available" && (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                )}
                {companyCheckStatus === "taken" && (
                  <XCircle className="h-4 w-4 text-red-500" />
                )}
              </div>
            </div>

            <AnimatePresence mode="wait">
              {!formData.companyName.trim() && touchedFields.companyName ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-start gap-1.5 text-xs text-amber-600"
                >
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>Please enter a company name to check availability</span>
                </motion.div>
              ) : companyCheckStatus === "checking" ? (
                <motion.div
                  key="checking"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-start gap-1.5 text-xs text-blue-600"
                >
                  <Loader2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 animate-spin" />
                  <span>Checking availability...</span>
                </motion.div>
              ) : companyCheckStatus === "available" ? (
                <motion.div
                  key="available"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-start gap-1.5 text-xs text-green-600 font-medium"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>Company name is available</span>
                </motion.div>
              ) : companyCheckStatus === "taken" ? (
                <motion.div
                  key="taken"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-start gap-1.5 text-xs text-red-500 font-medium"
                >
                  <XCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>{errors.companyName}</span>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>

          <div className="space-y-2">
            <Label htmlFor="signup-postalCode" className="text-sm font-medium">
              Postal Code
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <Input
              id="signup-postalCode"
              type="text"
              placeholder="Enter postal code"
              value={formData.postalCode}
              onChange={(e) => handleInputChange("postalCode", e.target.value)}
              onBlur={() => handleBlur("postalCode")}
              required
              disabled={isLoading}
              className={
                errors.postalCode ? "border-red-500 focus:ring-red-500" : ""
              }
            />
            <AnimatePresence>
              {errors.postalCode && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-start gap-1.5 text-xs text-red-500"
                >
                  <XCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>{errors.postalCode}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="signup-email" className="text-sm font-medium">
          Email Address
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <Input
          id="signup-email"
          type="email"
          placeholder="name@example.com"
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          onBlur={() => handleBlur("email")}
          required
          disabled={isLoading}
          className={errors.email ? "border-red-500 focus:ring-red-500" : ""}
        />
        <AnimatePresence>
          {errors.email && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-start gap-1.5 text-xs text-red-500"
            >
              <XCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{errors.email}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="signup-password" className="text-sm font-medium">
            Password
            <span className="text-red-500 ml-1">*</span>
          </Label>
          <div className="relative">
            <Input
              id="signup-password"
              type={showPasswords?.password ? "text" : "password"}
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              onBlur={() => handleBlur("password")}
              required
              maxLength={18}
              disabled={isLoading}
              className={`pr-10 ${
                errors.password ? "border-red-500 focus:ring-red-500" : ""
              }`}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
              onClick={() => handleShowPassword("password")}
              disabled={isLoading}
            >
              {showPasswords?.password ? (
                <EyeOff className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Eye className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          </div>
          <AnimatePresence>
            {errors.password && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-start gap-1.5 text-xs text-red-500"
              >
                <XCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>{errors.password}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm-password" className="text-sm font-medium">
            Confirm Password
            <span className="text-red-500 ml-1">*</span>
          </Label>
          <div className="relative">
            <Input
              id="confirm-password"
              type={showPasswords?.confirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={(e) =>
                handleInputChange("confirmPassword", e.target.value)
              }
              onBlur={() => handleBlur("confirmPassword")}
              required
              maxLength={18}
              disabled={isLoading}
              className={`pr-10 ${
                errors.confirmPassword
                  ? "border-red-500 focus:ring-red-500"
                  : ""
              }`}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
              onClick={() => handleShowPassword("confirmPassword")}
              disabled={isLoading}
            >
              {showPasswords?.confirmPassword ? (
                <EyeOff className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Eye className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          </div>
          <AnimatePresence>
            {errors.confirmPassword && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-start gap-1.5 text-xs text-red-500"
              >
                <XCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>{errors.confirmPassword}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-start space-x-2">
          <Checkbox
            id="terms-signup"
            checked={formData.termsAccepted}
            onCheckedChange={handleTermsChange}
            disabled={isLoading}
            className="mt-0.5"
          />
          <Label
            htmlFor="terms-signup"
            className="text-sm font-normal text-muted-foreground leading-relaxed cursor-pointer"
          >
            I accept the{" "}
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline hover:text-primary/80 font-medium"
            >
              Terms and Conditions
            </Link>
          </Label>
        </div>
        <AnimatePresence>
          {errors.termsAccepted && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-start gap-1.5 text-xs text-red-500 ml-6"
            >
              <XCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{errors.termsAccepted}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <DialogFooter className="pt-6 flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:space-x-3 px-0">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
          className="w-full sm:w-auto"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!isFormValid || isLoading || isCheckLoading}
          className="w-full sm:w-auto"
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Sign Up
        </Button>
      </DialogFooter>
    </form>
  );
};

export default SignupForm;
